import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';

const VALID_TICKET_STATUSES = new Set(['OPEN', 'IN_PROGRESS', 'WAITING_ON_USER', 'WAITING_ON_ADMIN', 'RESOLVED', 'CLOSED']);
const VALID_TICKET_PRIORITIES = new Set(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);

interface ApiErrorShape {
	response?: {
		status?: number;
		data?: {
			error?: string;
		};
	};
}

export interface PaginationInfo {
	page: number;
	size: number;
	totalElements: number;
	totalPages: number;
}

interface PagedResponse<T> {
	content?: T[];
	page?: number;
	size?: number;
	totalElements?: number;
	totalPages?: number;
}

export interface TicketAttachmentRecord extends Record<string, unknown> {
	fileId?: number | string | null;
	fileKey?: string | null;
	fileUrl?: string | null;
	fileName?: string | null;
	fileType?: string | null;
	fileSize?: number | null;
	uploadedBy?: number | null;
	status?: string | null;
	context?: string | null;
	entityType?: string | null;
	entityId?: number | null;
	createdOn?: string | null;
	updatedOn?: string | null;
}

export interface TicketCategoryRecord extends Record<string, unknown> {
	categoryNumber?: number | null;
	name?: string | null;
	description?: string | null;
	active?: boolean | null;
	createdOn?: string | null;
	updatedOn?: string | null;
}

export interface TicketCommentRecord extends Record<string, unknown> {
	commentNumber?: number | null;
	ticketNumber?: number | null;
	content?: string | null;
	internal?: boolean;
	createdOn?: string | null;
	createdBy?: number | null;
	createdByName?: string | null;
	createdByPhoto?: string | null;
	attachments: TicketAttachmentRecord[];
}

export interface SupportTicketRecord extends Record<string, unknown> {
	ticketNumber?: number | null;
	subject?: string | null;
	description?: string | null;
	status?: string | null;
	priority?: string | null;
	category?: TicketCategoryRecord | null;
	groupNumber?: number | null;
	assignedTo?: number | null;
	assignedToName?: string | null;
	assignedToPhoto?: string | null;
	escalatedToAdmin?: number | null;
	escalatedToAdminName?: string | null;
	escalatedToAdminPhoto?: string | null;
	firstResponseAt?: string | null;
	resolvedAt?: string | null;
	jiraIssueKey?: string | null;
	jiraIssueUrl?: string | null;
	createdOn?: string | null;
	updatedOn?: string | null;
	createdBy?: number | null;
	createdByName?: string | null;
	createdByPhoto?: string | null;
	updatedBy?: number | null;
	attachments: TicketAttachmentRecord[];
	comments: TicketCommentRecord[];
}

export interface TicketSlaRecord extends Record<string, unknown> {
	ticketNumber?: number | null;
	subject?: string | null;
	firstResponseMinutes?: number | null;
	resolutionMinutes?: number | null;
	hasFirstResponse?: boolean;
	resolved?: boolean;
}

export interface TicketTrendRecord {
	ticketNumber: number;
	createdOn: string | null;
	updatedOn: string | null;
}

export interface CreateTicketPayload {
	subject: string;
	description: string;
	categoryNumber?: number | null;
	priority?: string | null;
}

export interface UpdateTicketPayload {
	status?: string | null | undefined;
	priority?: string | null | undefined;
	assignedTo?: number | null | undefined;
	categoryNumber?: number | null | undefined;
}

export interface AddCommentPayload {
	content: string;
	internal: boolean;
	attachmentFileIds?: number[];
}

export interface TicketMutationResult<T = unknown> {
	success: boolean;
	data?: T;
	message?: string;
}

interface SupportTicketStoreState {
	trendLine: TicketTrendRecord[];
	myTickets: SupportTicketRecord[];
	myTicketsPagination: PaginationInfo | null;
	groupTickets: SupportTicketRecord[];
	groupTicketsPagination: PaginationInfo | null;
	allTickets: SupportTicketRecord[];
	allTicketsPagination: PaginationInfo | null;
	escalatedTickets: SupportTicketRecord[];
	escalatedTicketsPagination: PaginationInfo | null;
	currentTicket: SupportTicketRecord | null;
	ticketAttachments: TicketAttachmentRecord[];
	ticketAttachmentsPagination: PaginationInfo | null;
	slaMetrics: TicketSlaRecord[];
	slaPagination: PaginationInfo | null;
	loading: boolean;
	error: string | null;
}

type TicketListParams = Record<string, string | number | boolean | null | undefined>;

function normalizeTicketStatus(status: string | null | undefined): string | null | undefined {
	if (!status) return status;
	const normalized = (status satisfies string).trim().toUpperCase().replace(/\s+/g, '_');
	return VALID_TICKET_STATUSES.has(normalized) ? normalized : status;
}

function normalizeTicketPriority(priority: string | null | undefined): string | null | undefined {
	if (!priority) return priority;
	const normalized = (priority satisfies string).trim().toUpperCase().replace(/\s+/g, '_');
	return VALID_TICKET_PRIORITIES.has(normalized) ? normalized : priority;
}

function normalizeTicketComment(comment: Partial<TicketCommentRecord> | null | undefined): TicketCommentRecord {
	return {
		...(comment || {}),
		attachments: Array.isArray(comment?.attachments) ? comment.attachments : []
	};
}

function normalizeTicket(ticket: Partial<SupportTicketRecord> | null | undefined): SupportTicketRecord | null {
	if (!ticket || typeof ticket !== 'object') return null;
	return {
		...ticket,
		status: normalizeTicketStatus(ticket.status),
		priority: normalizeTicketPriority(ticket.priority),
		attachments: Array.isArray(ticket.attachments) ? ticket.attachments : [],
		comments: Array.isArray(ticket.comments) ? ticket.comments.map(normalizeTicketComment) : []
	} as SupportTicketRecord;
}

function buildPagination(payload: PagedResponse<unknown> | null | undefined, defaultSize = 20): PaginationInfo {
	return {
		page: payload?.page ?? 0,
		size: payload?.size ?? defaultSize,
		totalElements: payload?.totalElements ?? 0,
		totalPages: payload?.totalPages ?? 0
	};
}

function getErrorMessage(error: unknown, fallback: string): string {
	const apiError = error as ApiErrorShape;
	return apiError.response?.data?.error || fallback;
}

export const useSupportTicketStore = defineStore('supportTicketStore', {
	state: (): SupportTicketStoreState => ({
		trendLine: [],
		myTickets: [],
		myTicketsPagination: null,
		groupTickets: [],
		groupTicketsPagination: null,
		allTickets: [],
		allTicketsPagination: null,
		escalatedTickets: [],
		escalatedTicketsPagination: null,
		currentTicket: null,
		ticketAttachments: [],
		ticketAttachmentsPagination: null,
		slaMetrics: [],
		slaPagination: null,
		loading: false,
		error: null
	}),
	actions: {
		async fetchTrendLine(): Promise<TicketTrendRecord[]> {
			if (shouldThrottle('fetchTicketTrendLine', 100)) {
				posthog.capture('throttled_action', { action: 'fetchTicketTrendLine' });
				return this.trendLine;
			}
			this.error = null;
			try {
				const { data } = await apiClient.get<TicketTrendRecord[]>('/employee/support/tickets/trendLine');
				this.trendLine = data;
			} catch (error: unknown) {
				// The trendline endpoint returns 404 when no tickets exist.
				if ((error as ApiErrorShape).response?.status !== 404) {
					this.error = getErrorMessage(error, 'Failed to fetch ticket trend');
				}
				this.trendLine = [];
			}
			return this.trendLine;
		},

		async fetchMyTickets(params: TicketListParams = {}): Promise<SupportTicketRecord[]> {
			if (shouldThrottle('fetchMyTickets', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMyTickets' });
				return this.myTickets;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<PagedResponse<SupportTicketRecord>>('/tickets/my-tickets', { params });
				this.myTickets = (data.content || []).map((ticket) => normalizeTicket(ticket)).filter((ticket): ticket is SupportTicketRecord => ticket != null);
				this.myTicketsPagination = buildPagination(data);
				return this.myTickets;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch tickets');
				this.myTickets = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchGroupTickets(groupNumber: number, params: TicketListParams = {}): Promise<SupportTicketRecord[]> {
			if (shouldThrottle('fetchGroupTickets', 100)) {
				posthog.capture('throttled_action', { action: 'fetchGroupTickets' });
				return this.groupTickets;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<PagedResponse<SupportTicketRecord>>(`/tickets/group/${String(groupNumber)}`, { params });
				this.groupTickets = (data.content || []).map((ticket) => normalizeTicket(ticket)).filter((ticket): ticket is SupportTicketRecord => ticket != null);
				this.groupTicketsPagination = buildPagination(data);
				return this.groupTickets;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch group tickets');
				this.groupTickets = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchAllTickets(params: TicketListParams = {}): Promise<SupportTicketRecord[]> {
			if (shouldThrottle('fetchAllTickets', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllTickets' });
				return this.allTickets;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<PagedResponse<SupportTicketRecord>>('/tickets/all', { params });
				this.allTickets = (data.content || []).map((ticket) => normalizeTicket(ticket)).filter((ticket): ticket is SupportTicketRecord => ticket != null);
				this.allTicketsPagination = buildPagination(data);
				return this.allTickets;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch all tickets');
				this.allTickets = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchEscalatedTickets(params: TicketListParams = {}): Promise<SupportTicketRecord[]> {
			if (shouldThrottle('fetchEscalatedTickets', 100)) {
				posthog.capture('throttled_action', { action: 'fetchEscalatedTickets' });
				return this.escalatedTickets;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<PagedResponse<SupportTicketRecord>>('/tickets/escalated', { params });
				this.escalatedTickets = (data.content || []).map((ticket) => normalizeTicket(ticket)).filter((ticket): ticket is SupportTicketRecord => ticket != null);
				this.escalatedTicketsPagination = buildPagination(data);
				return this.escalatedTickets;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch escalated tickets');
				this.escalatedTickets = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchTicketDetail(ticketNumber: number): Promise<SupportTicketRecord | null> {
			if (shouldThrottle('fetchTicketDetail', 100)) {
				posthog.capture('throttled_action', { action: 'fetchTicketDetail' });
				return this.currentTicket;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<SupportTicketRecord>(`/tickets/${String(ticketNumber)}`);
				this.currentTicket = normalizeTicket(data);
				return this.currentTicket;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch ticket details');
				this.currentTicket = null;
				return null;
			} finally {
				this.loading = false;
			}
		},

		async createTicket(ticket: CreateTicketPayload): Promise<TicketMutationResult<SupportTicketRecord>> {
			if (shouldThrottle('createTicket', 100)) {
				posthog.capture('throttled_action', { action: 'createTicket' });
				return { success: false, message: 'Please wait before submitting again.' };
			}
			try {
				const { data } = await apiClient.post<SupportTicketRecord>('/tickets', ticket);
				const normalizedTicket = normalizeTicket(data);
				return normalizedTicket ? { success: true, data: normalizedTicket } : { success: false, message: 'Failed to create ticket' };
			} catch (error: unknown) {
				return { success: false, message: getErrorMessage(error, 'Failed to create ticket') };
			}
		},

		async updateTicket(ticketNumber: number, updates: UpdateTicketPayload): Promise<TicketMutationResult<SupportTicketRecord>> {
			if (shouldThrottle('updateTicket', 100)) {
				posthog.capture('throttled_action', { action: 'updateTicket' });
				return { success: false, message: 'Please wait before updating again.' };
			}
			try {
				const { data } = await apiClient.patch<SupportTicketRecord>(`/tickets/${String(ticketNumber)}`, updates, {
					headers: {
						'content-type': 'application/merge-patch+json'
					}
				});
				const normalizedTicket = normalizeTicket(data);
				if (!normalizedTicket) {
					return { success: false, message: 'Failed to update ticket' };
				}
				this.currentTicket = normalizedTicket;
				return { success: true, data: normalizedTicket };
			} catch (error: unknown) {
				return { success: false, message: getErrorMessage(error, 'Failed to update ticket') };
			}
		},

		async addComment(ticketNumber: number, comment: AddCommentPayload): Promise<TicketMutationResult<TicketCommentRecord>> {
			if (shouldThrottle('addComment', 100)) {
				posthog.capture('throttled_action', { action: 'addComment' });
				return { success: false, message: 'Please wait before commenting again.' };
			}
			try {
				const { data } = await apiClient.post<TicketCommentRecord>(`/tickets/${String(ticketNumber)}/comments`, comment);
				return { success: true, data: normalizeTicketComment(data) };
			} catch (error: unknown) {
				return { success: false, message: getErrorMessage(error, 'Failed to add comment') };
			}
		},

		async escalateToAdmin(ticketNumber: number, adminUserNumber: number): Promise<TicketMutationResult<SupportTicketRecord>> {
			if (shouldThrottle('escalateToAdmin', 100)) {
				posthog.capture('throttled_action', { action: 'escalateToAdmin' });
				return { success: false, message: 'Please wait before escalating again.' };
			}
			try {
				const { data } = await apiClient.post<SupportTicketRecord>(`/tickets/${String(ticketNumber)}/escalate-admin`, {
					adminUserNumber
				});
				const normalizedTicket = normalizeTicket(data);
				if (!normalizedTicket) {
					return { success: false, message: 'Failed to escalate ticket' };
				}
				this.currentTicket = normalizedTicket;
				return { success: true, data: normalizedTicket };
			} catch (error: unknown) {
				return { success: false, message: getErrorMessage(error, 'Failed to escalate ticket') };
			}
		},

		async fetchTicketAttachments(ticketNumber: number, params: TicketListParams = {}): Promise<TicketAttachmentRecord[]> {
			if (shouldThrottle(`fetchTicketAttachments_${String(ticketNumber)}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchTicketAttachments' });
				return this.ticketAttachments;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<PagedResponse<TicketAttachmentRecord>>(`/tickets/${String(ticketNumber)}/attachments`, { params });
				this.ticketAttachments = data.content || [];
				this.ticketAttachmentsPagination = buildPagination(data);
				return this.ticketAttachments;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch ticket attachments');
				this.ticketAttachments = [];
				this.ticketAttachmentsPagination = null;
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchSlaMetrics(params: TicketListParams = {}): Promise<TicketSlaRecord[]> {
			if (shouldThrottle('fetchSlaMetrics', 100)) {
				posthog.capture('throttled_action', { action: 'fetchSlaMetrics' });
				return this.slaMetrics;
			}
			this.loading = true;
			this.error = null;
			try {
				const { data } = await apiClient.get<TicketSlaRecord[] | PagedResponse<TicketSlaRecord>>('/tickets/sla/aggregate', { params });
				const metrics = Array.isArray(data) ? data : data.content || [];
				this.slaMetrics = metrics;
				const page = Number(params['page'] ?? 0);
				const size = Number(params['size'] ?? 20);
				const totalElements = Array.isArray(data) ? metrics.length : (data.totalElements ?? metrics.length);
				const totalPages = Array.isArray(data) ? (size > 0 ? Math.ceil(totalElements / size) : 0) : (data.totalPages ?? (size > 0 ? Math.ceil(totalElements / size) : 0));
				this.slaPagination = {
					page,
					size,
					totalElements,
					totalPages
				};
				return this.slaMetrics;
			} catch (error: unknown) {
				this.error = getErrorMessage(error, 'Failed to fetch SLA metrics');
				this.slaMetrics = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		clearCurrentTicket() {
			this.currentTicket = null;
			this.ticketAttachments = [];
			this.ticketAttachmentsPagination = null;
		}
	}
});
